import {
    Button,
    DrawerProps,
    Group,
    Select,
    Stack,
} from '@mantine/core';
import {PlatformDrawer} from '@flexinet/ui-components';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    InvoicesFilters,
    useInvoicesFilter,
} from '../hooks/useInvoicesFilter';

/**
 * FNP-918: the invoices filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useInvoicesFilter), not component state, so the table —
 * a sibling, not a child — filters the same list.
 *
 * ⚠️ Two fields are offered, both genuine enum/boolean states on the record:
 * `status` (Stripe's invoice lifecycle) and `paid`. Free-text fields are
 * deliberately absent — a filter control for `description` or `customerName`
 * would duplicate the keyword search, and building one would be serving a
 * hypothetical use case.
 *
 * ⚠️ Filtering here is CLIENT-SIDE over the loaded window. `InvoicesFilterInput`
 * does not declare these fields — the one thing it does narrow server-side is
 * `customerId`, which the customer detail tab passes as a prop rather than
 * through this drawer. See useInvoices.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useInvoicesFilter();

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Invoices')}
            size='md'
            {...rest}
        >
            <Formik<InvoicesFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Status')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'draft',
                                        label: I18n.get('Draft'),
                                    },
                                    {
                                        value: 'open',
                                        label: I18n.get('Open'),
                                    },
                                    {
                                        value: 'paid',
                                        label: I18n.get('Paid'),
                                    },
                                    {
                                        value: 'uncollectible',
                                        label: I18n.get('Uncollectible'),
                                    },
                                    {
                                        value: 'void',
                                        label: I18n.get('Void'),
                                    },
                                ]}
                                value={values.status ?? null}
                                onChange={(value) =>
                                    setFieldValue('status', value ?? '')}
                            />

                            <Select
                                label={I18n.get('Paid')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'true',
                                        label: I18n.get('Paid'),
                                    },
                                    {
                                        value: 'false',
                                        label: I18n.get('Unpaid'),
                                    },
                                ]}
                                value={values.paid ?? null}
                                onChange={(value) =>
                                    setFieldValue('paid', value ?? '')}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </PlatformDrawer>
    );
};
